import { Component, OnInit, OnChanges, OnDestroy, Input, Output, EventEmitter, SimpleChanges, ViewChild, TemplateRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { LlmService } from '../../services/llm.service';
import { FilterService } from '../../services/filter.service';

export interface FilterFieldConfig {
  paramKey: string;     // Outgoing backend query param key name
  extractKey?: string;  // Property on option object to extract (defaults to 'id')
}

export type InferenceFilterRegistry = Record<string, Record<string, FilterFieldConfig>>;

const INFERENCE_FILTER_MAPS: InferenceFilterRegistry = {
  risk: {
    project: { paramKey: 'project_id', extractKey: 'id' },
    cycle: { paramKey: 'cycle', extractKey: 'id' },
    domain: { paramKey: 'risk_domain', extractKey: 'id' },
    subdomain: { paramKey: 'risk_sub_domain', extractKey: 'id' },
    risk: { paramKey: 'risk', extractKey: 'id' }
  },
  audit: {
    name: { paramKey: 'name' },
    domain: { paramKey: 'domain', extractKey: 'id' },
    subdomain: { paramKey: 'sub_domain', extractKey: 'id' },
  },
  compliance: {
    regulator: { paramKey: 'regulator_id', extractKey: 'id' },
    document: { paramKey: 'document_id', extractKey: 'id' },
    obligation: { paramKey: 'obligation_id', extractKey: 'id' },
    control_parameter: { paramKey: 'control_parameter_id', extractKey: 'id' },
  },
  assessment: {
    portfolio: { paramKey: 'portfolio', extractKey: 'id' },
    subportfolio: { paramKey: 'subportfolio', extractKey: 'id' },
    program: { paramKey: 'program', extractKey: 'id' },
    project: { paramKey: 'project', extractKey: 'id' },
    name: { paramKey: 'name' },
  }
};

@Component({
  selector: 'app-llm-inference',
  templateUrl: './llm-inference.component.html',
  styleUrls: ['./llm-inference.component.scss']
})
export class LlmInferenceComponent implements OnInit, OnChanges, OnDestroy {
  @Input() inferenceType: string;
  @Input() outputRef: any;
  @Input() cycleId: any;
  @Input() cycle: any;
  @Input() promptVars: any = {};
  @Input() pollInterval: number = 2000;
  @Input() viewMode: 'short' | 'full' | 'table' | 'popup' = 'short';
  @Input() viewContext?: string;
  @Input() buttonLabel: string = 'Airo Cue';
  @Input() displayLabels: any = {};
  @Input() customFilterMap?: Record<string, FilterFieldConfig>;

  @Output() taskCompleted = new EventEmitter<any>();
  @Output() taskFailed = new EventEmitter<any>();

  loading: boolean = false;
  polling: boolean = false;
  taskId: string = '';
  status: string = '';
  result: any = null;
  error: string = '';

  tableHeaders: any[] = [];
  tableData: any[] = [];
  groupedDomains: any[] = [];

  // Pagination matching risk-tracker
  CurrentPage: number = 1;
  sortRecords: number = 10;
  sortOptions: number[] = [10, 25, 50, 100];
  startValue: number = 0;
  endValue: number = 10;
  totalCount: number = 0;

  filterParams: any = {};
  private pollTimer: any = null;
  private taskSub: Subscription | null = null;
  private filterSub: Subscription | null = null;

  @ViewChild('evidenceDialog') evidenceDialogRef!: TemplateRef<any>;
  @ViewChild('dialogTemplate') dialogTemplateRef!: TemplateRef<any>;
  selectedEvidenceRow: any = null;
  evidenceDialogInstance: MatDialogRef<any> | null = null;
  evidenceLoading: boolean = false;
  evidenceError: string = '';
  evidenceData: any = null;

  previewFile: any = null;
  safePreviewUrl: SafeResourceUrl | null = null;
  pdfData: Uint8Array | null = null;
  isLoadingPdf: boolean = false;

  constructor(
    private readonly llmService: LlmService,
    private readonly filterService: FilterService,
    private readonly dialog: MatDialog,
    private readonly sanitizer: DomSanitizer,
    private readonly http: HttpClient
  ) { }

  openDialog(): void {
    if (!this.dialogTemplateRef) return;
    this.dialog.open(this.dialogTemplateRef, {
      width: '1150px',
      maxHeight: '90vh'
    });
  }

  openEvidenceDialog(row: any): void {
    this.selectedEvidenceRow = row;
    this.evidenceLoading = true;
    this.evidenceError = '';
    this.evidenceData = null;
    this.closeFilePreview();

    this.evidenceDialogInstance = this.dialog.open(this.evidenceDialogRef, {
      width: '1150px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      panelClass: 'evidence-dialog-panel'
    });

    const params: any = {
      inference_type: this.inferenceType
    };

    if (this.outputRef) {
      this.assignDefined(params, this.outputRef);
    }

    if (row) {
      if (row.project_risk_id) params.project_risk_id = row.project_risk_id;
      else if (row.id) params.project_risk_id = row.id;
      if (row.source_ref) this.assignDefined(params, row.source_ref);
      if (row.output_ref) this.assignDefined(params, row.output_ref);
    }

    this.llmService.getEvidence(params).subscribe(
      (res: any) => {
        this.evidenceLoading = false;
        this.evidenceData = res;
      },
      (err: any) => {
        this.evidenceLoading = false;
        this.evidenceError = err?.error?.error || 'Failed to fetch evidence details.';
      }
    );
  }

  getEvidenceSummaryText(): string {
    if (this.evidenceData?.summary) {
      const s = this.formatSummaryVal(this.evidenceData.summary);
      if (s) return s;
    }
    if (this.selectedEvidenceRow) {
      const summary = this.extractSummaryText(this.selectedEvidenceRow);
      if (summary && !summary.includes('Insufficient detail')) {
        return summary;
      }
    }
    return '';
  }

  previewSelectedFile(file: any): void {
    if (!file) return;
    this.previewFile = file;
    this.pdfData = null;

    if (this.evidenceDialogInstance) {
      this.evidenceDialogInstance.updateSize('1150px', '85vh');
    }

    const s3Key = file.s3_key || file.key;
    if (this.isPdf(file)) {
      this.isLoadingPdf = true;
      if (s3Key) {
        this.llmService.downloadEvidenceFile(s3Key).subscribe({
          next: (blob: Blob) => {
            const fileReader = new FileReader();
            fileReader.onload = () => {
              this.isLoadingPdf = false;
              this.pdfData = new Uint8Array(fileReader.result as ArrayBuffer);
            };
            fileReader.onerror = () => {
              this.isLoadingPdf = false;
            };
            fileReader.readAsArrayBuffer(blob);
          },
          error: (err: any) => {
            this.isLoadingPdf = false;
            console.error('Error loading PDF via backend:', err);
          }
        });
      } else if (file.url) {
        fetch(file.url)
          .then(res => res.blob())
          .then(blob => {
            const fileReader = new FileReader();
            fileReader.onload = () => {
              this.isLoadingPdf = false;
              this.pdfData = new Uint8Array(fileReader.result as ArrayBuffer);
            };
            fileReader.onerror = () => {
              this.isLoadingPdf = false;
            };
            fileReader.readAsArrayBuffer(blob);
          })
          .catch(() => {
            this.isLoadingPdf = false;
          });
      } else {
        this.isLoadingPdf = false;
      }
    }
  }

  closeFilePreview(): void {
    this.previewFile = null;
    this.safePreviewUrl = null;
    this.pdfData = null;
    this.isLoadingPdf = false;
    if (this.evidenceDialogInstance) {
      this.evidenceDialogInstance.updateSize('1150px', '90vh');
    }
  }

  downloadFile(file: any): void {
    const s3Key = file?.s3_key || file?.key;
    if (s3Key) {
      this.llmService.downloadEvidenceFile(s3Key).subscribe({
        next: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = file.file_name || 'evidence-file';
          a.click();
          window.URL.revokeObjectURL(url);
        },
        error: (err: any) => {
          console.error('Error downloading file:', err);
          if (file?.url) window.open(file.url, '_blank');
        }
      });
    } else if (file?.url) {
      window.open(file.url, '_blank');
    }
  }

  isPdf(file: any): boolean {
    if (!file) return false;
    const name = typeof file === 'string' ? file : (file.file_name || file.s3_key || file.name || '');
    return name.toLowerCase().includes('.pdf');
  }

  isImage(file: any): boolean {
    if (!file) return false;
    const name = (typeof file === 'string' ? file : (file.file_name || file.s3_key || file.name || '')).toLowerCase();
    return name.includes('.png') || name.includes('.jpg') || name.includes('.jpeg') || name.includes('.gif') || name.includes('.webp');
  }

  isString(val: any): boolean {
    return typeof val === 'string';
  }

  ngOnInit(): void {
    if (this.viewMode === 'table') {
      this.filterSub = this.filterService.filterValues.subscribe((filterObj: any) => {
        this.filterParams = this.extractFilterParams(filterObj);
        this.CurrentPage = 1;
        this.loadExistingResult();
      });
    } else if (this.outputRef) {
      this.loadExistingResult();
    }

    this.taskSub = this.llmService.taskDispatched$.subscribe((data: any) => {
      if (
        data &&
        data.inferenceType === this.inferenceType &&
        (this.viewMode === 'table' || this.isSameOutputRef(data.outputRef, this.outputRef))
      ) {
        if (data.status === 'LOADING') {
          this.loading = true;
          this.error = '';
          this.result = null;
        } else if (data.status === 'DONE') {
          this.loading = false;
          this.error = '';
          if (this.viewMode === 'table') {
            this.loadExistingResult();
          } else {
            this.result = data.result;
          }
          this.stopPolling();
        } else if (data.status === 'FAILED') {
          this.loading = false;
          this.error = data.error || 'Inference task failed';
          this.stopPolling();
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const hasChanged = (changes.outputRef && !changes.outputRef.firstChange) ||
      (changes.cycleId && !changes.cycleId.firstChange) ||
      (changes.cycle && !changes.cycle.firstChange);
    if (hasChanged) {
      if (this.outputRef || this.viewMode === 'table') {
        this.loadExistingResult();
      }
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
    if (this.taskSub) {
      this.taskSub.unsubscribe();
    }
    if (this.filterSub) {
      this.filterSub.unsubscribe();
    }
  }

  private extractFilterParams(filterObj: any): any {
    const params: any = {};
    if (!filterObj?.inputs) return params;

    const mappingConfig = this.customFilterMap || INFERENCE_FILTER_MAPS[this.inferenceType] || {};

    for (const input of filterObj.inputs) {
      if (!input.value || (Array.isArray(input.value) && input.value.length === 0)) continue;

      const fieldConfig = mappingConfig[input.field];
      if (fieldConfig) {
        const extractKey = fieldConfig.extractKey || 'id';
        const ids = this.extractInputIds(input.value, extractKey);
        if (ids.length) {
          params[fieldConfig.paramKey] = ids.join(',');
        }
      }
    }
    return params;
  }

  private extractInputIds(values: any, key: string = 'id'): any[] {
    if (!values) return [];
    const arr = Array.isArray(values) ? values : [values];
    return arr.map((v: any) => {
      if (v && typeof v === 'object') {
        return v[key] !== undefined ? v[key] : (v.id !== undefined ? v.id : (v.value !== undefined ? v.value : v));
      }
      return v;
    });
  }

  private isSameOutputRef(ref1: any, ref2: any): boolean {
    if (!ref1 || !ref2) return false;
    return JSON.stringify(ref1) === JSON.stringify(ref2);
  }

  cellSpansMap: Map<string, { rowspan: number; hide: boolean }> = new Map();

  isIndexCol(_col: any): boolean {
    return false;
  }

  isSummaryCol(col: any): boolean {
    if (!col) return false;
    const key = this.getColKey(col).toLowerCase().trim();
    const label = this.getColLabel(col).toLowerCase().trim();
    return (
      key === 'summary' ||
      key === 'ai_summary' ||
      key === 'ai summary' ||
      key === 'inference' ||
      key.includes('summary') ||
      label.includes('summary') ||
      label.includes('inference')
    );
  }

  isSummaryOrLastCol(col: any): boolean {
    if (this.isSummaryCol(col)) return true;
    const headers = (this.tableHeaders && this.tableHeaders.length > 0) ? this.tableHeaders : this.getDefaultHeaders();
    if (headers && headers.length > 0) {
      const lastCol = headers[headers.length - 1];
      return this.getColKey(col) === this.getColKey(lastCol);
    }
    return false;
  }

  isGroupableCol(col: any): boolean {
    if (typeof col === 'object' && col !== null && col.groupable !== undefined) {
      return Boolean(col.groupable);
    }
    return !this.isIndexCol(col) && !this.isSummaryCol(col);
  }

  getDefaultHeaders(): any[] {
    if (this.tableData && this.tableData.length > 0) {
      const sample = this.tableData[0];
      const keys = Object.keys(sample).filter(k => k !== 'result' && k !== 'task_id' && k !== 'inference_type' && k !== '#');
      if (keys.length > 0) {
        return keys.map(k => ({
          key: k,
          label: this.formatHeaderLabel(k),
          groupable: !this.isSummaryCol(k)
        }));
      }
    }
    return [];
  }

  formatHeaderLabel(key: string): string {
    if (key === '#') return '#';
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  }

  get hasIndexCol(): boolean {
    return false;
  }

  getIndexColLabel(): string {
    return '#';
  }

  computeCellSpans(): void {
    this.cellSpansMap.clear();
    if (!this.tableData || this.tableData.length === 0) {
      return;
    }

    const headers = (this.tableHeaders && this.tableHeaders.length > 0) ? this.tableHeaders : this.getDefaultHeaders();
    if (!headers || headers.length === 0) return;

    for (let r = 0; r < this.tableData.length; r++) {
      for (const col of headers) {
        const key = this.getColKey(col);
        this.cellSpansMap.set(`${r}_${key}`, { rowspan: 1, hide: false });
      }
    }

    for (let r = 0; r < this.tableData.length; r++) {
      let parentGroupMerged = true;

      for (let cIdx = 0; cIdx < headers.length; cIdx++) {
        const col = headers[cIdx];
        const key = this.getColKey(col);

        if (!this.isGroupableCol(col)) {
          parentGroupMerged = false;
          continue;
        }

        if (r > 0 && parentGroupMerged) {
          const currentVal = this.getCellValue(this.tableData[r], col, r);
          const prevVal = this.getCellValue(this.tableData[r - 1], col, r - 1);

          if (currentVal !== '-' && currentVal === prevVal) {
            let rootR = r - 1;
            while (rootR > 0 && this.cellSpansMap.get(`${rootR}_${key}`)?.hide) {
              rootR--;
            }

            const rootSpan = this.cellSpansMap.get(`${rootR}_${key}`);
            if (rootSpan) {
              rootSpan.rowspan += 1;
            }

            this.cellSpansMap.set(`${r}_${key}`, { rowspan: 0, hide: true });
          } else {
            parentGroupMerged = false;
          }
        } else {
          parentGroupMerged = false;
        }
      }
    }
  }

  getCellSpan(rowIndex: number, col: any): { rowspan: number; hide: boolean } {
    const key = this.getColKey(col);
    return this.cellSpansMap.get(`${rowIndex}_${key}`) || { rowspan: 1, hide: false };
  }

  extractSummaryText(row: any, colKey?: string, colLabel?: string): string {
    if (!row) return 'Insufficient detail to generate meaningful inference for Airo Cue.';

    if (colKey && row[colKey] !== undefined && row[colKey] !== null) {
      const val = this.formatSummaryVal(row[colKey]);
      if (val) return val;
    }
    if (colLabel && row[colLabel] !== undefined && row[colLabel] !== null) {
      const val = this.formatSummaryVal(row[colLabel]);
      if (val) return val;
    }

    if (row.summary !== undefined) {
      const val = this.formatSummaryVal(row.summary);
      if (val) return val;
    }
    if (row.ai_summary !== undefined) {
      const val = this.formatSummaryVal(row.ai_summary);
      if (val) return val;
    }
    if (row.short_summary !== undefined) {
      const val = this.formatSummaryVal(row.short_summary);
      if (val) return val;
    }

    if (row.result) {
      if (typeof row.result === 'string' && row.result.trim().length > 0) return row.result;
      if (typeof row.result === 'object') {
        if (row.result.summary) {
          const val = this.formatSummaryVal(row.result.summary);
          if (val) return val;
        }
        if (row.result.short_summary) {
          const val = this.formatSummaryVal(row.result.short_summary);
          if (val) return val;
        }
        if (row.result.points) {
          const val = this.formatSummaryVal(row.result.points);
          if (val) return val;
        }
      }
    }

    for (const key of Object.keys(row)) {
      const kLower = key.toLowerCase();
      if (kLower === 'inference_type' || kLower === 'inference_id' || kLower === 'task_id' || kLower === 'status') {
        continue;
      }
      if (kLower.includes('summary')) {
        const val = this.formatSummaryVal(row[key]);
        if (val) return val;
      }
    }
    return 'Insufficient detail to generate meaningful inference for Airo Cue.';
  }

  private formatSummaryVal(val: any): string | null {
    if (val === null || val === undefined) return null;
    if (typeof val === 'string') {
      const trimmed = val.trim();
      return trimmed.length > 0 ? trimmed : null;
    }
    if (Array.isArray(val)) {
      const joined = val.join(' ').trim();
      return joined.length > 0 ? joined : null;
    }
    if (typeof val === 'object') {
      if (val.summary) return this.formatSummaryVal(val.summary);
      if (val.text) return this.formatSummaryVal(val.text);
      return JSON.stringify(val);
    }
    return String(val);
  }

  getColKey(col: any): string {
    if (typeof col === 'string') return col;
    return col?.key || col?.field || col?.name || col?.label || '';
  }

  getColLabel(col: any): string {
    if (typeof col === 'string') return col;
    return col?.label || col?.header || col?.title || col?.key || col?.field || '';
  }

  getCellValue(row: any, col: any, idx: number): string {
    if (!row) return '-';

    if (this.isIndexCol(col)) {
      return row['#'] !== undefined ? row['#'] : (row['index'] !== undefined ? row['index'] : (idx + 1).toString());
    }

    const colKey = this.getColKey(col);
    const colLabel = this.getColLabel(col);

    if (this.isSummaryCol(col)) {
      return this.extractSummaryText(row, colKey, colLabel);
    }

    if (row[colKey] !== undefined && row[colKey] !== null && row[colKey] !== '') {
      return this.formatVal(row[colKey]);
    }
    if (row[colLabel] !== undefined && row[colLabel] !== null && row[colLabel] !== '') {
      return this.formatVal(row[colLabel]);
    }

    const normKey = colKey.toLowerCase().trim().replace(/[\s-]+/g, '_');
    const normLabel = colLabel.toLowerCase().trim().replace(/[\s-]+/g, '_');

    if (row[normKey] !== undefined && row[normKey] !== null && row[normKey] !== '') {
      return this.formatVal(row[normKey]);
    }
    if (row[normLabel] !== undefined && row[normLabel] !== null && row[normLabel] !== '') {
      return this.formatVal(row[normLabel]);
    }

    const cleanKey = normKey.replace(/_/g, '');
    const cleanLabel = normLabel.replace(/_/g, '');

    for (const k of Object.keys(row)) {
      const cleanK = k.toLowerCase().replace(/[\s_-]+/g, '');
      if (cleanK === cleanKey || cleanK === cleanLabel) {
        const val = row[k];
        if (val !== undefined && val !== null && val !== '') {
          return this.formatVal(val);
        }
      }
    }

    if (row.result && typeof row.result === 'object') {
      const resObj = row.result;
      if (resObj[colKey] !== undefined) return this.formatVal(resObj[colKey]);
      if (resObj[normKey] !== undefined) return this.formatVal(resObj[normKey]);
    }

    return '-';
  }

  private formatVal(val: any): string {
    if (typeof val === 'object' && val !== null) {
      return val.name || val.title || val.summary || JSON.stringify(val);
    }
    return String(val);
  }

  get summaryPoints(): string[] {
    if (!this.result) return [];

    const summaryData =
      this.result.short_summary ||
      this.result.summary ||
      this.result.points ||
      (this.result.result && (this.result.result.short_summary || this.result.result.summary));

    if (Array.isArray(summaryData)) {
      return summaryData;
    }

    if (typeof summaryData === 'string') {
      return summaryData
        .split('\n')
        .map(line => line.replace(/^[\s•\-\*\d+\.]+\s*/, '').trim())
        .filter(line => line.length > 0);
    }

    return [];
  }

  getColor(item: any): string | null {
    if (!item) return null;
    const color = (item.color || (item.result && item.result.color))?.toString().toLowerCase().trim();
    if (color === 'green') return '#22c55e';
    if (color === 'yellow') return '#f59e0b';
    if (color === 'red') return '#ef4444';
    return null;
  }

  get statusColor(): string | null {
    return this.getColor(this.result);
  }

  get summaryText(): string {
    if (!this.result) return '';
    return this.result.summary || (typeof this.result.short_summary === 'string' ? this.result.short_summary : '');
  }

  get recommendationsPoints(): string[] {
    if (!this.result) return [];

    const recs = this.result.recommendations || (this.result.result && this.result.result.recommendations);
    if (Array.isArray(recs)) {
      return recs;
    }
    if (typeof recs === 'string') {
      return recs
        .split('\n')
        .map(line => line.replace(/^[\s•\-\*\d+\.]+\s*/, '').trim())
        .filter(line => line.length > 0);
    }

    return [];
  }

  private extractResultData(res: any): any {
    if (!res) return null;

    if (Array.isArray(res.results) && res.results.length > 0) {
      const firstItem = res.results[0];
      return firstItem?.result || firstItem;
    }

    if (res.result !== undefined && res.result !== null) {
      return res.result;
    }

    return res;
  }

  loadExistingResult(): void {
    if (!this.outputRef && this.viewMode !== 'table') return;

    this.loading = true;
    this.error = '';
    this.result = null;

    if (this.viewMode === 'table') {
      this.startValue = (this.CurrentPage - 1) * parseInt(this.sortRecords as any, 10);
      this.endValue = this.startValue + parseInt(this.sortRecords as any, 10);
    }

    const params: any = {
      inference_type: this.inferenceType
    };

    if (this.cycleId || this.cycle) {
      params.cycle = this.cycleId || this.cycle;
    }

    if (this.viewContext) {
      params.view_context = this.viewContext;
    }

    if (this.viewMode === 'table') {
      params.offset = this.startValue;
      params.index = this.endValue;
      this.assignDefined(params, this.filterParams);
    }

    if (this.outputRef) {
      this.assignDefined(params, this.outputRef);
    }

    this.llmService.getInferenceResult(params).subscribe(
      (res: any) => {
        this.loading = false;

        if (!res || !res.data) {
          this.status = '';
          this.result = null;
          this.tableData = [];
          this.totalCount = 0;
          return;
        }

        this.status = 'DONE';

        if (this.viewMode === 'table') {
          this.tableData = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : [res?.data || res]);
          this.tableHeaders = (res && res.headers && res.headers.length > 0) ? res.headers : this.getDefaultHeaders();
          this.totalCount = res?.count !== undefined ? res.count : this.tableData.length;
          this.computeCellSpans();
        } else {
          this.result = this.extractResultData(res?.data || res);
        }
        this.taskCompleted.emit(this.viewMode === 'table' ? this.tableData : this.result);
      },
      (_err: any) => {
        this.loading = false;
        this.status = '';
        this.result = null;
        this.tableData = [];
        this.totalCount = 0;
      }
    );
  }

  getRecords(): void {
    this.loadExistingResult();
  }

  onAiroCueClick(): void {
    if (localStorage.getItem("airoCueEnabled")?.toLowerCase() === "true") {
      this.dispatchTask();
    } else {
      this.loadExistingResult();
    }
  }

  dispatchTask(force: boolean = false): void {
    if (!this.outputRef) {
      return;
    }

    this.stopPolling();
    this.loading = true;
    this.error = '';
    this.result = null;

    this.llmService.notifyTaskDispatched({
      status: 'LOADING',
      inferenceType: this.inferenceType,
      outputRef: this.outputRef
    });

    const payload = {
      inference_type: this.inferenceType,
      output_ref: this.assignDefined({}, this.outputRef),
      prompt_vars: this.promptVars,
      force
    };

    this.llmService.dispatchTask(payload).subscribe(
      (res: any) => {
        this.taskId = res.task_id;
        this.status = res.status || 'PENDING';

        if (this.status === 'DONE') {
          this.fetchTaskResult();
        } else if (this.status === 'FAILED') {
          this.loading = false;
          this.error = res.error || 'Inference task failed';
          this.taskFailed.emit(this.error);
          this.llmService.notifyTaskDispatched({
            status: 'FAILED',
            inferenceType: this.inferenceType,
            outputRef: this.outputRef,
            error: this.error
          });
        } else {
          this.startPolling();
        }
      },
      (err: any) => {
        this.loading = false;
        this.error = err?.error?.error || err?.message || 'Failed to dispatch task';
        this.taskFailed.emit(this.error);
        this.llmService.notifyTaskDispatched({
          status: 'FAILED',
          inferenceType: this.inferenceType,
          outputRef: this.outputRef,
          error: this.error
        });
      }
    );
  }

  fetchTaskResult(): void {
    if (!this.taskId) {
      this.loading = false;
      return;
    }

    this.llmService.getTaskById(this.taskId).subscribe(
      (res: any) => {
        this.loading = false;
        this.status = res.status;
        this.result = this.extractResultData(res);
        this.taskCompleted.emit(this.result);
        this.llmService.notifyTaskDispatched({
          status: 'DONE',
          inferenceType: this.inferenceType,
          outputRef: this.outputRef,
          result: this.result
        });
      },
      (err: any) => {
        this.loading = false;
        this.error = err?.error?.error || 'Failed to fetch task result';
        this.taskFailed.emit(this.error);
        this.llmService.notifyTaskDispatched({
          status: 'FAILED',
          inferenceType: this.inferenceType,
          outputRef: this.outputRef,
          error: this.error
        });
      }
    );
  }

  startPolling(): void {
    this.stopPolling();
    this.polling = true;

    this.pollTimer = setInterval(() => {
      if (!this.taskId) {
        this.stopPolling();
        return;
      }

      this.llmService.getTaskById(this.taskId).subscribe(
        (res: any) => {
          this.status = res.status;
          if (res.status === 'DONE') {
            this.stopPolling();
            this.loading = false;
            this.result = this.extractResultData(res);
            this.taskCompleted.emit(this.result);
            this.llmService.notifyTaskDispatched({
              status: 'DONE',
              inferenceType: this.inferenceType,
              outputRef: this.outputRef,
              result: this.result
            });
          } else if (res.status === 'FAILED') {
            this.stopPolling();
            this.loading = false;
            this.error = res.error || 'Inference task failed';
            this.taskFailed.emit(this.error);
            this.llmService.notifyTaskDispatched({
              status: 'FAILED',
              inferenceType: this.inferenceType,
              outputRef: this.outputRef,
              error: this.error
            });
          }
        },
        (_err: any) => {
          // Continue polling unless explicit hard error occurs
        }
      );
    }, this.pollInterval);
  }

  stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    this.polling = false;
  }

  private assignDefined(target: any, source: any): any {
    if (!source || typeof source !== 'object') return target;
    for (const key of Object.keys(source)) {
      const val = source[key];
      if (val !== null && val !== undefined && val !== '' && val !== 'null' && val !== 'undefined') {
        target[key] = val;
      }
    }
    return target;
  }

  retry(): void {
    this.dispatchTask(true);
  }
}
